import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Layout,
  PageHeader,
  Tabs,
  Button,
  Spin,
  message,
  Modal,
  Breadcrumb,
  notification
} from 'antd';
import style from './index.scss';
import Typologies from './TypologiesTab';
import RiskIndicators from './RiskIndicatorsTab';
import DataSet from './DatasetTab';
import AlertScoringTab from './AlertScoringTab';
import { CONFIRM_PROPS, SCENARIO_FAILED, RETURN_TO_HOME } from './constants';
import {
  dateTimeFormat,
  TM_FRAUD_SOURCE_IDS
} from '../../../util/GlobalConstant';
import { convertLocalTimeToEpoch } from '../../../util/timezone';
import DynamicProperties from '../../common/DynamicProperties';
import ThresholdGenerationBanner from './ThresholdGenerationBanner';
import ScenarioValidationErrorModal from './ScenarioValidationErrorModal';
import { hasAttributeValueByAttributeKey } from '../../../util';
import { getSelectedResources } from '../../../util/resourceManager';
import { getStrategyType } from '../../../util/localStorageUtils';
import { isEmpty } from '../../../util/lodashlike';

const { TabPane } = Tabs;
const { TM_UI_RI_DYNPROPS, TM_UI_TYPOLOGY_DYNPROPS, trmResources } =
  getSelectedResources();

const ConfigureScenario = ({
  createRunInstanceLiveSets,
  trmDashboard,
  scenario,
  scenarioAPIActions = {},
  configureRunInstanceAPIActions = {},
  match = { params: {} },
  isLoading,
  error,
  history,
  authentication,
  typologySegment,
  thresholdGeneration = {},
  thresholdGenerationActions = {},
  // FIN-11803: Launch-gate modal slice + dispatcher wired from
  // configureInstanceDashboard/index.js.
  scenarioValidationErrors = { visible: false, errors: [] }
}) => {
  const {
    fetchSingleScenario,
    triggerScenariosInit,
    triggerScenarios,
    hideValidationErrors,
    refreshScenarioValidation
  } = scenarioAPIActions;

  const typologiesData =
    trmDashboard.scenarioTypologies?.data?.segmentCombinations;

  const generationState = thresholdGeneration?.generationState || 'configuring';
  const isScenarioLocked =
    generationState === 'generating' || generationState === 'generated';

  const [runDataSet, setRunDataSet] = useState(null);
  const [tabActiveKey, setTabActiveKey] = useState('1');
  const [isValueEdited, setIsValueEdited] = useState(false);
  const [resetRI, setResetRI] = useState(false);
  const [preventionAccess, setPreventionAccess] = useState(false);
  // FIN-11922: tracks that a Launch was fired from THIS view so the
  // navigation effect below only reacts to the launch we initiated (not a
  // stale/initial triggeredScenarioStatus).
  const [launchAttempted, setLaunchAttempted] = useState(false);
  const returnToRunInstance = () => {
    history.push(RETURN_TO_HOME);
  };

  const scenarioID = match.params.id;

  const checkTabAccess = (uiResource) => {
    if (!authentication?.data) return false;

    const strategyType = getStrategyType();

    // For Keycloak, check if the resource operations include the uiResource
    if (strategyType === 'keycloak') {
      const hasAccess = authentication?.data?.some((item) =>
        item?.details?.resource?.operations?.includes(uiResource)
      );
      return hasAccess;
    } else {
      // For Hybrid, check the resourceCode directly
      const targetResourceObject = authentication?.data?.find(
        (item) => item?.details?.resource?.resourceCode === uiResource
      );
      return !!targetResourceObject;
    }
  };

  useEffect(() => {
    fetchSingleScenario({
      scenarioID: match.params.id
    });
    configureRunInstanceAPIActions.fetchScenarioTypologies({
      id: match.params.id
    });
    // FIN-11922: populate the Typologies-tab validation badges on load so
    // failing typologies/variants are flagged (red cross) without first
    // needing a save or a Launch/Generate attempt.
    refreshScenarioValidation({ scenarioID: match.params.id });
  }, [match.params.id]);

  useEffect(() => {
    const preventionAccess = hasAttributeValueByAttributeKey(
      authentication,
      trmResources.APP_3_RESOURCE_UI_RUN_INSTANCE,
      'PRODUCT_SOURCE_ID',
      TM_FRAUD_SOURCE_IDS.FRAUD_PREV_ID
    );
    if (!preventionAccess) {
      configureRunInstanceAPIActions.getLiveSets();
    }
    setPreventionAccess(preventionAccess);
  }, [authentication]);

  useEffect(() => {
    if (!isEmpty(error) && !isLoading) {
      message.error(SCENARIO_FAILED, 1.5, () => {
        returnToRunInstance();
      });
    }
  }, [error]);

  useEffect(() => () => triggerScenariosInit(), []);

  // FIN-11922: the Launch saga (FIN-11800) runs GET /scenarios/:id/validate
  // BEFORE executing and, on failure, dispatches showValidationErrors +
  // addTriggerScenarioFail('validation') instead of launching. Navigation must
  // therefore wait for that ASYNC result — the previous code called
  // returnToRunInstance() synchronously inside onLaunch, which unmounted this
  // view (and its ScenarioValidationErrorModal) before /validate resolved, so
  // the modal never appeared. Now we only leave on a real launch success; on a
  // validation failure we stay here so the modal (FIN-11802) is shown.
  useEffect(() => {
    if (!launchAttempted) return;
    const status = trmDashboard?.triggeredScenarioStatus || {};
    if (status.isLoading) return;
    if (status.error) {
      // Validation (or launch) failure — stay on Configure; the modal renders
      // from the scenarioValidationErrors slice the saga just populated.
      setLaunchAttempted(false);
    } else if (status.data) {
      // Launch accepted — return to the run-instance list as before.
      setLaunchAttempted(false);
      returnToRunInstance();
    }
  }, [trmDashboard?.triggeredScenarioStatus, launchAttempted]);

  useEffect(() => {
    if (preventionAccess && scenario?.id) {
      thresholdGenerationActions.fetchThresholdStatus({
        scenarioId: scenario.id
      });
      thresholdGenerationActions.fetchThresholdResults({
        scenarioId: scenario.id
      });
      // FIN-12229 — also load the saved config so the "Thresholds Generated" banner can show the
      // dataset date range (config.execStartDate/execEndDate) used for the last generation.
      thresholdGenerationActions.fetchThresholdConfig({
        scenarioId: scenario.id
      });
    }
  }, [scenario?.id, preventionAccess]);

  useEffect(() => {
    return () => thresholdGenerationActions.thresholdGenerationReset();
  }, []);

  const dateSetOnChange = (value) => {
    setRunDataSet({
      ...runDataSet,
      ...value
    });
  };

  const checkEditedValue = (value) => {
    setIsValueEdited(value);
  };

  const onTabChange = (selectedTabKey) => {
    if (selectedTabKey !== '2') {
      if (isValueEdited) {
        Modal.confirm({
          ...CONFIRM_PROPS,
          onOk: () => {
            setIsValueEdited(false);
            setResetRI(true);
            setTabActiveKey(selectedTabKey);
          },
          onCancel: () => {
            setTabActiveKey('2');
          }
        });
        return;
      }
    }
    if (resetRI) {
      setResetRI(false);
    }
    setTabActiveKey(selectedTabKey);
  };

  const backToHome = (evt) => {
    evt.preventDefault();
    if (isValueEdited) {
      Modal.confirm({
        ...CONFIRM_PROPS,
        onOk: () => {
          setIsValueEdited(false);
          returnToRunInstance();
        }
      });
      return;
    }
    returnToRunInstance();
  };

  const validateTypologyBeforeSave = () => {
    if (typologiesData.filter((f) => f.useDefault).length === 0) {
      notification.open({
        message: (
          <p>
            <i style={{ color: '#e74c3c' }} className="fa fa-close"></i>
            &nbsp;&nbsp; {'Error!!'}
          </p>
        ),
        description: 'Please select at least one typology for default segment!'
      });
      return false;
    }

    const selectedCombination =
      createRunInstanceLiveSets?.data?.combinations?.filter((combinations) =>
        typologiesData.some((typology) =>
          typology.combinations.map((t) => t.id).includes(combinations.id)
        )
      );

    if (
      createRunInstanceLiveSets?.data?.combinations &&
      selectedCombination.length !==
        createRunInstanceLiveSets?.data?.combinations?.length
    ) {
      notification.open({
        message: (
          <p>
            <i style={{ color: '#e74c3c' }} className="fa fa-close"></i>
            &nbsp;&nbsp; {'Error!!'}
          </p>
        ),
        description: 'Please select at least one typology for segment sets!'
      });
      return false;
    }

    return true;
  };

  const onLaunch = () => {
    const { trainingDates, predictionDates } = runDataSet;
    const [fromTrainingDate, toTrainingDate] = trainingDates?.item;
    const [fromPredictionDate, toPredictionDate] = predictionDates?.item;

    if (
      !preventionAccess &&
      !scenario?.latestInstance &&
      !validateTypologyBeforeSave()
    ) {
      return;
    }

    triggerScenarios({
      scenarioID: scenario.id,
      prediction: {
        dateRange: {
          from: convertLocalTimeToEpoch(
            fromPredictionDate.format(dateTimeFormat)
          ),
          to: convertLocalTimeToEpoch(toPredictionDate.format(dateTimeFormat))
        }
      },
      training: {
        dateRange: {
          from: convertLocalTimeToEpoch(
            fromTrainingDate.format(dateTimeFormat)
          ),
          to: convertLocalTimeToEpoch(toTrainingDate.format(dateTimeFormat))
        }
      }
    });
    // FIN-11922: do NOT navigate here. Let the effect above react to the
    // launch-gate result — navigate only on success, stay (and show the
    // validation modal) on failure.
    setLaunchAttempted(true);
  };

  return (
    <Layout className={style.runInstanceConfiglayout}>
      {/* FIN-11803: Launch-gate modal. Saga (FIN-11800) dispatches
          SHOW_VALIDATION_ERRORS when the new /scenarios/:id/validate
          endpoint returns a non-empty errors list. OK closes the modal
          via hideValidationErrors and leaves the user on Configure to
          fix the listed variants. */}
      <ScenarioValidationErrorModal
        visible={scenarioValidationErrors.visible}
        errors={scenarioValidationErrors.errors}
        warnings={scenarioValidationErrors.warnings}
        onOk={hideValidationErrors}
      />
      <PageHeader
        ghost={false}
        title={
          <Breadcrumb separator=">">
            <Breadcrumb.Item href="/app/trm/run-instance" onClick={backToHome}>
              Run Instance Name
            </Breadcrumb.Item>
            <Breadcrumb.Item>Configure Scenario</Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={[
          <Button
            type="primary"
            onClick={onLaunch}
            key="onLaunch"
            // FIN-12061: show a loading spinner and disable while the launch
            // (validate + execute) saga is in flight, so the slow execute API
            // can't be triggered multiple times. launchAttempted is true from
            // the moment a launch fires until triggeredScenarioStatus resolves
            // (success → navigate, failure → modal), both of which reset it.
            loading={launchAttempted}
            // FIN-12390: the run-instance Launch is independent of threshold
            // generation, so it must NOT be gated by `isGenerating`. Threshold
            // generation is a separate feature-extraction pipeline
            // (handleThresholdGeneration) and its `generating` state persists
            // across refresh — coupling it here left Launch permanently greyed
            // once a generation had been triggered. Gate only on the launch's
            // own prerequisites (double-submit guard + both date ranges); the BE
            // concurrent-launch guard handles any real conflict.
            style={
              launchAttempted ||
              !(runDataSet?.trainingDates && runDataSet.predictionDates)
                ? {}
                : { backgroundColor: '#0c8ed7' }
            }
            disabled={
              launchAttempted ||
              !(runDataSet?.trainingDates && runDataSet.predictionDates)
            }>
            Launch
          </Button>
        ]}></PageHeader>
      {preventionAccess && (
        <ThresholdGenerationBanner
          generationState={thresholdGeneration.generationState}
          startedAt={thresholdGeneration.startedAt}
          isTriggering={thresholdGeneration.isTriggering}
          generatedAt={thresholdGeneration?.results?.createdAt}
          dataStartDate={thresholdGeneration?.config?.execStartDate}
          dataEndDate={thresholdGeneration?.config?.execEndDate}
          scenarioId={scenario?.id}
          onTrigger={thresholdGenerationActions.triggerThresholdGeneration}
          onDiscard={thresholdGenerationActions.discardThresholdGeneration}
        />
      )}
      <Spin spinning={isLoading}>
        <Tabs
          className={style.configNav}
          activeKey={tabActiveKey}
          destroyInactiveTabPane
          onChange={onTabChange}
          style={{ minHeight: 550 }}>
          <TabPane tab="Typologies" key="1">
            {scenario && (
              <Typologies
                scenarioName={scenario.name}
                scenarioId={scenarioID}
                executed={!!scenario.latestInstance}
                scenarioSource={scenario.sourceId}
                preventionAccess={preventionAccess}
                typologiesList={typologiesData}
                configureRunInstanceActions={configureRunInstanceAPIActions}
                liveSets={createRunInstanceLiveSets}
                dynpropsTabAccess={checkTabAccess(TM_UI_TYPOLOGY_DYNPROPS)}
                isScenarioLocked={isScenarioLocked}
                generationState={generationState}
                thresholdResults={thresholdGeneration?.results}
              />
            )}
          </TabPane>
          {!preventionAccess && (
            <TabPane tab="Risk Indicators" key="2">
              {scenario && (
                <RiskIndicators
                  scenarioId={scenario.id}
                  checkEditedValue={checkEditedValue}
                  onCancel={returnToRunInstance}
                  reset={resetRI}
                  scenarioSource={scenario.sourceId}
                  scenarioTypologies={trmDashboard?.scenarioTypologies}
                />
              )}
            </TabPane>
          )}
          <TabPane tab="Dataset" key="3">
            {scenario && (
              <DataSet
                onChange={dateSetOnChange}
                trainingDays={runDataSet?.trainingDates?.days}
                predictionDays={runDataSet?.predictionDates?.days}
                scenarioSource={scenario.sourceId}
              />
            )}
          </TabPane>
          {checkTabAccess(TM_UI_RI_DYNPROPS) && (
            <TabPane tab="Dynamic Properties" key="4" destroyInactiveTabPane>
              {scenario && tabActiveKey === '4' && (
                <DynamicProperties
                  subModule="run_instance"
                  moduleId="3"
                  runInstanceId={scenarioID}
                />
              )}
            </TabPane>
          )}
          {preventionAccess && (
            <TabPane tab="Alert Scoring" key="5">
              {scenario && (
                <AlertScoringTab
                  scenarioId={scenarioID}
                  isScenarioLocked={isScenarioLocked}
                />
              )}
            </TabPane>
          )}
        </Tabs>
      </Spin>
    </Layout>
  );
};

ConfigureScenario.propTypes = {
  scenario: PropTypes.object,
  scenarioAPIActions: PropTypes.object,
  isLoading: PropTypes.bool,
  match: PropTypes.object,
  error: PropTypes.object,
  history: PropTypes.object,
  trmDashboard: PropTypes.object,
  createRunInstanceLiveSets: PropTypes.object,
  configureRunInstanceAPIActions: PropTypes.object,
  authentication: PropTypes.object,
  createRunInstanceCategories: PropTypes.object,
  typologySegment: PropTypes.object,
  thresholdGeneration: PropTypes.object,
  thresholdGenerationActions: PropTypes.object
};

export default ConfigureScenario;
